/**
 * TAILWIND PAGINATION KOMPONENTE
 *
 * Basiert auf Tailwind UI "Page navigation with border-top" Vorlage.
 *
 * Layout (Desktop):
 *   [<- Zurück]           [1] [2] [3] ... [10]           [Weiter ->]
 *
 * Props:
 *   currentPage          - number, aktuelle Seite (1-basiert)
 *   totalPages           - number, Gesamtanzahl Seiten
 *   onPageChange         - (page: number) => void
 *   visiblePages?        - number (default: 7), Anzahl sichtbarer Seiten-Buttons
 *   showInfo?            - boolean (default: false), zeigt "Seite X von Y" auf Mobile
 *   totalItems?          - number, für Info-Text
 *   itemsPerPage?        - number, für Info-Text
 *   className?           - zusätzliche Klassen
 *
 * Usage:
 *   <TWPagination currentPage={page} totalPages={10} onPageChange={setPage} />
 */
import React from 'react';
import { ArrowLongLeftIcon, ArrowLongRightIcon } from '@heroicons/react/20/solid';

export interface TWPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Anzahl der sichtbaren Seiten-Buttons (Standard: 7) */
  visiblePages?: number;
  /** Zeigt "Seite X von Y" auf Mobile */
  showInfo?: boolean;
  totalItems?: number;
  itemsPerPage?: number;
  /** @deprecated Zeilen-Auswahl gehört in die Filter-Bar, nicht in die Pagination */
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  /** @deprecated Zeilen-Auswahl gehört in die Filter-Bar, nicht in die Pagination */
  itemsPerPageOptions?: number[];
  /** Klasse für Container */
  className?: string;
}

export const TWPagination: React.FC<TWPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  visiblePages = 7,
  showInfo = false,
  totalItems,
  itemsPerPage,
  className = '',
}) => {
  // Keine Pagination wenn nur eine Seite
  if (totalPages <= 1) return null;

  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];

    if (totalPages <= visiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      const middleStart = Math.max(2, currentPage - 1);
      const middleEnd = Math.min(totalPages - 1, currentPage + 1);

      if (middleStart > 2) {
        pages.push('ellipsis');
      }

      for (let i = middleStart; i <= middleEnd; i++) {
        pages.push(i);
      }

      if (middleEnd < totalPages - 1) {
        pages.push('ellipsis');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const getInfoText = () => {
    if (!totalItems || !itemsPerPage) return null;
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalItems);
    return `${start}-${end} von ${totalItems}`;
  };

  return (
    <nav
      className={`flex items-center justify-between border-t border-gray-200 dark:border-white/10 px-4 pb-4 ${className}`}
      aria-label="Pagination"
    >
      {/* Links: Zurück */}
      <div className="-mt-px flex w-0 flex-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`
            inline-flex items-center border-t-2 border-transparent pt-4 pr-1 text-sm font-medium transition-colors
            ${currentPage === 1
              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              : 'text-gray-400 hover:border-gray-300 dark:hover:border-white/20 hover:text-gray-200'
            }
          `}
        >
          <ArrowLongLeftIcon aria-hidden="true" className="mr-3 size-5 text-gray-500" />
          <span className="hidden sm:inline">Zurück</span>
        </button>
      </div>

      {/* Mitte: Seiten-Buttons (Desktop) */}
      <div className="hidden md:-mt-px md:flex">
        {pageNumbers.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500"
              >
                ...
              </span>
            );
          }

          const isActive = page === currentPage;
          return (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              aria-current={isActive ? 'page' : undefined}
              className={`
                inline-flex items-center justify-center border-t-2 px-4 pt-4 pb-2 min-w-[44px] min-h-[44px] text-sm font-medium transition-colors
                ${isActive
                  ? 'border-brand text-brand'
                  : 'border-transparent text-gray-400 hover:border-gray-300 dark:hover:border-white/20 hover:text-gray-200'
                }
              `}
            >
              {page}
            </button>
          );
        })}
      </div>

      {/* Mobile: Seitenanzeige */}
      <div className="flex md:hidden -mt-px items-center pt-4">
        {showInfo && totalItems && itemsPerPage ? (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {getInfoText()}
          </span>
        ) : (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Seite {currentPage} von {totalPages}
          </span>
        )}
      </div>

      {/* Rechts: Weiter */}
      <div className="-mt-px flex w-0 flex-1 justify-end">
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`
            inline-flex items-center border-t-2 border-transparent pt-4 pl-1 text-sm font-medium transition-colors
            ${currentPage === totalPages
              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              : 'text-gray-400 hover:border-gray-300 dark:hover:border-white/20 hover:text-gray-200'
            }
          `}
        >
          <span className="hidden sm:inline">Weiter</span>
          <ArrowLongRightIcon aria-hidden="true" className="ml-3 size-5 text-gray-500" />
        </button>
      </div>
    </nav>
  );
};

export default TWPagination;
